import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import { useBundleStore } from './bundle'
import { showBundleSummary } from '@/utils/bundle'
import { isSubscriptionItem, isBundleItem, sumQuantity, jsonPayload } from '@/utils'
import { OON_COFFEE_BUNDLE_SKU } from '@/services/constants'
import { getTemplateData } from '@/utils/dom'
import { CART_ITEM_ADD, track } from '@/services/events'
import { useAmplitude } from '@/services/amplitude'

import type { Product, Variant } from '@/types/shopify'
import type { Cart, CartItem, CartUpdatePayload, SimpleCustomer } from '@/types/shopify/cart'

type LoadingState = 'checkout' | 'update' | 'cart' | 'add' | undefined

export const useCartStore = defineStore('cart', () => {
  const bundleStore = useBundleStore()
  const { trackCart } = useAmplitude()
  const loading = ref<LoadingState>()
  const cart = ref<Cart>()
  const landingPage = ref<string>()
  const customer = ref(getTemplateData<SimpleCustomer>('#json-customer'))

  const items = computed(() => cart.value?.items || [])
  const subscriptionItems = computed(() => items.value.filter(isSubscriptionItem))
  const nonBundleItems = computed(() => items.value.filter(item => !isBundleItem(item)))
  const nonBundleItemsPrice = computed(() => nonBundleItems.value.reduce((acc, item) => acc + (item.price * item.quantity), 0))

  const isFirstOrder = computed(() => (customer.value?.order_count || 0) === 0)
  const isSubscriptionOrder = computed(() => subscriptionItems.value.length)
  const subExCount = computed(() => sumQuantity(subscriptionItems.value))
  const displayNotification = computed(() => Boolean(nonBundleItems.value.length || bundleStore.totalQuantity))

  const totalPrice = computed(() => bundleStore.priceCents + nonBundleItemsPrice.value)
  const subscriptionTotalPrice = computed(() => bundleStore.pricing.firstOrderCents + nonBundleItemsPrice.value)
  const onetimeTotalPrice = computed(() => bundleStore.pricing.onetimeReferralCents + nonBundleItemsPrice.value)

  const cartItemsAnalyticsData = computed(() => items.value.map((item: CartItem) => ({
    sku: item.sku,
    quantity: item.quantity
  })))

  const clearOonCarryover = async () => {
    if (!cart.value) return

    const oonItems = cart.value.items.filter(item => OON_COFFEE_BUNDLE_SKU.test(String(item.sku)))
    if (!oonItems.length) return

    const updates: CartUpdatePayload['updates'] = {}
    for (const item of oonItems) {
      updates[item.id] = 0
    }
    const response = await fetch('/cart/update.json', jsonPayload('post', {
      updates,
      attributes: { domain: window.location.hostname }
    }))
    cart.value = await response.json()
  }

  const load = async () => {
    loading.value = 'cart'
    const response = await fetch('/cart.json')
    cart.value = await response.json()
    await clearOonCarryover()
    loading.value = undefined
  }

  const update = async (payload: CartUpdatePayload) => {
    loading.value = 'update'
    const response = await fetch('/cart/update.json', jsonPayload('post', payload))
    cart.value = await response.json()
    loading.value = undefined
  }

  const removeBundle = async () => {
    const bundle = items.value.find(isBundleItem)
    if (bundle) {
      await update({ updates: { [bundle.id]: 0 } })
    }
  }

  const add = async (product?: Product, variant?: Variant | CartItem, properties?: Record<string, string | number>) => {
    if (!(product && variant)) {
      return
    }
    loading.value = 'add'
    await fetch('/cart/add.json', jsonPayload('post', { id: variant.id, properties }))
    await load()
    if (cart.value) {
      track(CART_ITEM_ADD, cart.value)
      await trackCart(cart.value)
    }
    loading.value = undefined
    showBundleSummary()
  }

  return {
    load,
    update,
    removeBundle,
    add,
    cart,
    landingPage,
    customer,
    items,
    subscriptionItems,
    nonBundleItems,
    loading,
    isFirstOrder,
    isSubscriptionOrder,
    subExCount,
    nonBundleItemsPrice,
    totalPrice,
    subscriptionTotalPrice,
    onetimeTotalPrice,
    displayNotification,
    cartItemsAnalyticsData
  }
}, {
  persist: {
    storage: window.sessionStorage,
    pick: [ 'landingPage' ],
    afterHydrate: ({ store }) => {
      if (!store.landingPage) {
        store.landingPage = window.location.pathname
      }
    }
  }
})
